import React, {useEffect, useRef, useState} from "react";

interface TimePadWidgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
}

export const TimePadWidgetModal: React.FC<TimePadWidgetModalProps> = ({
  isOpen,
  onClose,
  eventId,
}) => {
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const [useFallback, setUseFallback] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !eventId) return;

    const container = widgetContainerRef.current;
    if (!container) return;

    setIsLoading(true);
    setUseFallback(false);

    const script = document.createElement("script");
    script.defer = true;
    script.setAttribute("data-timepad-widget-v2", "event_register");
    script.src = "https://timepad.ru/js/tpwf/loader/min/loader.js";
    script.charset = "UTF-8";

    script.textContent = `(function(){return {
      "event":{"id":"${eventId}"},
      "debug": false
    };})();`;

    script.onload = () => {
      console.log("Скрипт TimePad loader загружен для события:", eventId);

      setTimeout(() => {
        const currentContainer = widgetContainerRef.current;
        if (currentContainer) {
          const hasIframe = currentContainer.querySelector("iframe");
          const hasChildren = currentContainer.children.length > 0;
          
          if (hasIframe || hasChildren) {
            console.log("Виджет TimePad успешно инициализирован");
            setIsLoading(false);
          } else {
            setTimeout(() => {
              const stillContainer = widgetContainerRef.current;
              if (stillContainer) {
                const stillHasIframe = stillContainer.querySelector("iframe");
                const stillHasChildren = stillContainer.children.length > 0;
                
                if (stillHasIframe || stillHasChildren) {
                  console.log("Виджет TimePad инициализирован (второй чек)");
                  setIsLoading(false);
                } else {
                  console.warn("Виджет TimePad не создал контент");
                  setIsLoading(false);
                  setUseFallback(true);
                }
              }
            }, 2000);
          }
        }
      }, 1500);
    };

    script.onerror = () => {
      console.error("Ошибка загрузки скрипта TimePad loader");
      setIsLoading(false);
      setUseFallback(true);
    };

    container.appendChild(script);
    scriptRef.current = script;

    const checkTimeout = setTimeout(() => {
      const currentContainer = widgetContainerRef.current;
      if (currentContainer) {
        const hasIframe = currentContainer.querySelector("iframe");
        const hasChildren = currentContainer.children.length > 0;
        
        if (!hasIframe && !hasChildren) {
          console.warn("Виджет TimePad не загрузился в течение 10 секунд");
          setIsLoading(false);
          setUseFallback(true);
        } else if (hasIframe || hasChildren) {
          setIsLoading(false);
        }
      }
    }, 10000);

    return () => {
      clearTimeout(checkTimeout);

      scriptRef.current = null;
      
      setIsLoading(true);
      setUseFallback(false);
    };
  }, [isOpen, eventId]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 text-2xl font-bold z-10"
          onClick={onClose}
          aria-label="Закрыть"
        >
          ×
        </button>
        {useFallback ? (
          <div className="mt-4 p-6 text-center">
            <p className="text-gray-700 mb-4">
              Не удалось загрузить форму регистрации. Вы можете зарегистрироваться на странице события.
            </p>
            <a
              href={`https://yanzone.timepad.ru/event/${eventId}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 py-3 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              onClick={onClose}
            >
              Перейти на страницу регистрации
            </a>
          </div>
        ) : (
          <div className="mt-4 min-h-[400px]">
            {isLoading && (
              <div className="flex items-center justify-center h-[400px] text-gray-500">
                Загрузка формы регистрации...
              </div>
            )}
            <div ref={widgetContainerRef} className="min-h-[400px]" />
          </div>
        )}
      </div>
    </div>
  );
};

